import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  TrendingUp,
  ShoppingBag,
  Receipt,
  Download,
  Music,
  ShieldAlert,
  ArrowLeft,
  Loader2,
  Search,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANGES = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 365, label: '1 year' },
];

// Business statuses (canonical). Key = display value, value = tailwind classes.
// We ONLY render these — raw Maya values like PAYMENT_SUCCESS never appear in the UI.
const STATUS_TONE = {
  'Paid':             'bg-[#00F0FF]/10 text-[#00F0FF] border-[#00F0FF]/30',
  'Pending':          'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
  'Checkout Created': 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  'Failed':           'bg-red-500/10 text-red-400 border-red-500/30',
  'Cancelled':        'bg-[#27272A] text-[#A1A1AA] border-[#3F3F46]',
};
const statusChip = (s) =>
  STATUS_TONE[s] || 'bg-[#27272A] text-[#A1A1AA] border-[#3F3F46]';

const SEVERITY_TONE = {
  high: 'border-red-500/40 bg-red-500/5 text-red-300',
  medium: 'border-yellow-500/40 bg-yellow-500/5 text-yellow-300',
  low: 'border-[#27272A] bg-[#0F0F11] text-[#A1A1AA]',
};

const formatPhp = (n) =>
  `₱${Number(n || 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-PH', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

const KPI = ({ icon: Icon, label, value, sub, testid, accent = '#00F0FF' }) => (
  <div
    className="rounded-xl border border-[#18181B] bg-[#0B0B0D] p-5 hover:border-[#27272A] transition-colors"
    data-testid={testid}
  >
    <div className="flex items-center gap-3 mb-3">
      <div
        className="w-9 h-9 rounded-md flex items-center justify-center"
        style={{ background: `${accent}18`, color: accent }}
      >
        <Icon size={16} />
      </div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{label}</p>
    </div>
    <p className="text-2xl md:text-3xl font-heading font-medium text-white">{value}</p>
    {sub && <p className="text-[11px] text-[#71717A] mt-1">{sub}</p>}
  </div>
);

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [fangates, setFangates] = useState([]);
  const [fraud, setFraud] = useState([]);
  const [orderStatus, setOrderStatus] = useState('all');
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);

  const fetchAll = async (opts = {}) => {
    const isInit = !stats;
    if (isInit) setLoading(true);
    else setRefreshing(true);
    try {
      const [statsRes, ordersRes, fgRes, fraudRes] = await Promise.all([
        axios.get(`${API}/admin/stats?days=${days}`, { withCredentials: true }),
        axios.get(`${API}/admin/orders?status=${opts.status || orderStatus}&days=${days}`, {
          withCredentials: true,
        }),
        axios.get(`${API}/admin/fangates?days=${days}`, { withCredentials: true }),
        axios.get(`${API}/admin/fraud-signals`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data.orders);
      setFangates(fgRes.data.fangates);
      setFraud(fraudRes.data.signals);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error('Session expired');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const fetchOrders = async (statusOverride) => {
    setOrdersLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/orders?status=${statusOverride ?? orderStatus}&days=${days}${
          query ? `&q=${encodeURIComponent(query)}` : ''
        }`,
        { withCredentials: true }
      );
      setOrders(data.orders);
    } catch (e) {
      toast.error('Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  const openOrder = async (order) => {
    setSelectedOrder(order);
    setSelectedOrderDetail(null);
    try {
      const { data } = await axios.get(`${API}/admin/orders/${order.id}`, {
        withCredentials: true,
      });
      setSelectedOrderDetail(data);
    } catch (e) {
      toast.error('Failed to load order detail');
    }
  };

  const kpi = stats?.kpi;

  const paidRate = useMemo(() => {
    if (!kpi || !kpi.total_orders) return 0;
    return Math.round((kpi.paid_orders / kpi.total_orders) * 100);
  }, [kpi]);

  return (
    <div className="min-h-screen bg-[#09090B] py-8 px-4 lg:px-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="text-[#00F0FF]" size={30} />
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-medium tracking-tight" data-testid="dashboard-title">
                Sales & Orders
              </h1>
              <p className="text-[#A1A1AA] text-sm">
                Monitor revenue, buyer activity, and fraud signals in one place.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-full border border-[#27272A] bg-[#0B0B0D] p-0.5" data-testid="range-selector">
              {RANGES.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setDays(r.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    days === r.value ? 'bg-[#00F0FF] text-black' : 'text-[#A1A1AA] hover:text-white'
                  }`}
                  data-testid={`range-${r.value}d`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              onClick={() => fetchAll()}
              disabled={refreshing || loading}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="refresh-btn"
            >
              <RefreshCw size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/releases')}
              className="border-[#27272A] hover:bg-[#18181B] rounded-full"
              data-testid="back-releases-btn"
            >
              <ArrowLeft size={14} className="mr-1.5" /> Releases
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="animate-spin text-[#00F0FF]" size={28} />
          </div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <KPI
                icon={TrendingUp}
                label={`Revenue · ${days}d`}
                value={formatPhp(kpi.revenue_in_range)}
                sub={`All-time ${formatPhp(kpi.total_revenue)}`}
                testid="kpi-revenue"
              />
              <KPI
                icon={ShoppingBag}
                label="Paid Orders"
                value={kpi.paid_orders_in_range}
                sub={`${paidRate}% paid rate · ${kpi.total_orders} total`}
                testid="kpi-orders"
                accent="#22c55e"
              />
              <KPI
                icon={Receipt}
                label="Avg. Order Value"
                value={formatPhp(kpi.avg_order_value)}
                sub={`Range: ${days} days`}
                testid="kpi-aov"
                accent="#a855f7"
              />
              <KPI
                icon={Download}
                label="Free Unlocks"
                value={kpi.free_unlocks_in_range}
                sub={`All-time ${kpi.free_unlocks_total} · ${kpi.free_sessions_total} attempts`}
                testid="kpi-unlocks"
                accent="#f59e0b"
              />
            </div>

            {/* Chart + Top */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-2 rounded-xl border border-[#18181B] bg-[#0B0B0D] p-5" data-testid="revenue-chart">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-heading font-medium">Revenue trend</h3>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                    Last {days} days
                  </span>
                </div>
                <div className="h-64 -mx-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={stats.revenue_series}>
                      <defs>
                        <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#18181B" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: '#52525B', fontSize: 10 }}
                        axisLine={{ stroke: '#18181B' }}
                        tickFormatter={(d) => d.slice(5)}
                      />
                      <YAxis
                        tick={{ fill: '#52525B', fontSize: 10 }}
                        axisLine={{ stroke: '#18181B' }}
                        width={45}
                        tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)}
                      />
                      <Tooltip
                        contentStyle={{
                          background: '#0F0F11',
                          border: '1px solid #27272A',
                          borderRadius: 6,
                          fontSize: 12,
                        }}
                        labelStyle={{ color: '#A1A1AA' }}
                        formatter={(v) => [formatPhp(v), 'Revenue']}
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="#00F0FF"
                        strokeWidth={2}
                        fill="url(#revFill)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="rounded-xl border border-[#18181B] bg-[#0B0B0D] p-5" data-testid="top-albums">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-heading font-medium">Top sellers</h3>
                  <Music size={14} className="text-[#71717A]" />
                </div>
                {stats.top_albums.length === 0 ? (
                  <p className="text-xs text-[#71717A] py-8 text-center">
                    No sales yet — top-selling tracks will appear here.
                  </p>
                ) : (
                  <ol className="space-y-3">
                    {stats.top_albums.slice(0, 6).map((a, i) => (
                      <li
                        key={a.album_id}
                        className="flex items-center gap-3"
                        data-testid={`top-album-${i}`}
                      >
                        <span className="w-5 text-xs text-[#52525B]">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{a.title}</p>
                          <p className="text-[10px] text-[#71717A]">
                            {a.sales_count} sold · {formatPhp(a.revenue)}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>

            {/* Fraud signals */}
            <div className="rounded-xl border border-[#18181B] bg-[#0B0B0D] p-5 mb-8" data-testid="fraud-panel">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ShieldAlert size={16} className="text-yellow-400" />
                  <h3 className="text-sm font-heading font-medium">Fraud signals</h3>
                </div>
                <span className="text-xs text-[#71717A]" data-testid="fraud-count">
                  {fraud.length} {fraud.length === 1 ? 'signal' : 'signals'}
                </span>
              </div>
              {fraud.length === 0 ? (
                <p className="text-xs text-[#71717A] py-4">
                  Nothing suspicious detected in the last 24 hours.
                </p>
              ) : (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {fraud.map((s, i) => (
                    <li
                      key={i}
                      className={`rounded-lg border p-3 text-xs ${SEVERITY_TONE[s.severity]}`}
                      data-testid={`fraud-signal-${i}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="uppercase tracking-widest text-[10px] font-medium">
                          {s.severity}
                        </span>
                        <span className="text-white font-medium">{s.title}</span>
                      </div>
                      <p className="text-[#A1A1AA]">{s.detail}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Orders */}
            <div className="rounded-xl border border-[#18181B] bg-[#0B0B0D] mb-8" data-testid="orders-panel">
              <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-[#18181B]">
                <h3 className="text-sm font-heading font-medium">Buyer orders</h3>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <Search
                      size={12}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]"
                    />
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchOrders()}
                      placeholder="Search order id, session, album…"
                      className="pl-8 pr-3 py-1.5 text-xs bg-[#0F0F11] border border-[#27272A] rounded-md focus:border-[#00F0FF] focus:outline-none w-56 text-white placeholder:text-[#52525B]"
                      data-testid="orders-search"
                    />
                  </div>
                  <select
                    value={orderStatus}
                    onChange={(e) => {
                      setOrderStatus(e.target.value);
                      fetchOrders(e.target.value);
                    }}
                    className="px-3 py-1.5 text-xs bg-[#0F0F11] border border-[#27272A] rounded-md focus:border-[#00F0FF] focus:outline-none text-white"
                    data-testid="orders-status-filter"
                  >
                    <option value="all">All statuses</option>
                    <option value="Paid">Paid</option>
                    <option value="Pending">Pending</option>
                    <option value="Checkout Created">Checkout Created</option>
                    <option value="Failed">Failed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="orders-table">
                  <thead className="bg-[#0F0F11] text-[10px] uppercase tracking-[0.15em] text-[#52525B]">
                    <tr>
                      <th className="text-left px-4 py-3">Order</th>
                      <th className="text-left px-4 py-3">Buyer</th>
                      <th className="text-left px-4 py-3">Session</th>
                      <th className="text-left px-4 py-3">Items</th>
                      <th className="text-right px-4 py-3">Amount</th>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-left px-4 py-3">Downloads</th>
                      <th className="text-left px-4 py-3">Placed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordersLoading ? (
                      <tr>
                        <td colSpan={8} className="text-center py-10">
                          <Loader2 className="animate-spin text-[#00F0FF] inline" size={16} />
                        </td>
                      </tr>
                    ) : orders.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-10 text-xs text-[#71717A]">
                          No orders in this range.
                        </td>
                      </tr>
                    ) : (
                      orders.map((o) => (
                        <tr
                          key={o.id}
                          onClick={() => openOrder(o)}
                          className="border-t border-[#18181B] hover:bg-[#0F0F11] cursor-pointer transition-colors"
                          data-testid={`order-row-${o.id}`}
                        >
                          <td className="px-4 py-3 font-mono text-xs text-[#D4D4D8]">
                            {o.id.slice(0, 8)}…
                          </td>
                          <td className="px-4 py-3 text-xs min-w-0" data-testid={`order-buyer-${o.id}`}>
                            {o.email ? (
                              <div className="max-w-[220px]">
                                <div className="text-white truncate">{o.full_name || '—'}</div>
                                <div className="text-[#71717A] truncate">{o.email}</div>
                              </div>
                            ) : (
                              <span className="text-[#52525B]">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-[#A1A1AA]" data-testid={`order-session-${o.id}`}>
                            {o.session_masked}
                          </td>
                          <td className="px-4 py-3 text-xs text-[#D4D4D8]">
                            {o.items?.length || 0} × item
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            {formatPhp(o.total_amount)}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${statusChip(o.business_status || o.status)}`}
                              data-testid={`order-status-${o.id}`}
                            >
                              {o.business_status || 'Pending'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-[#A1A1AA]">
                            {o.download_links_accessed}/{o.download_links_count}
                          </td>
                          <td className="px-4 py-3 text-xs text-[#71717A]">
                            {formatDate(o.created_at)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Fangate unlocks */}
            <div className="rounded-xl border border-[#18181B] bg-[#0B0B0D]" data-testid="fangates-panel">
              <div className="flex items-center justify-between p-5 border-b border-[#18181B]">
                <h3 className="text-sm font-heading font-medium">Free download unlocks</h3>
                <span className="text-xs text-[#71717A]">
                  {fangates.length} in last {days} days
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#0F0F11] text-[10px] uppercase tracking-[0.15em] text-[#52525B]">
                    <tr>
                      <th className="text-left px-4 py-3">Album</th>
                      <th className="text-left px-4 py-3">Session</th>
                      <th className="text-left px-4 py-3">Socials</th>
                      <th className="text-left px-4 py-3">Unlocked</th>
                      <th className="text-left px-4 py-3">Started</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fangates.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-10 text-xs text-[#71717A]">
                          No free-download attempts yet.
                        </td>
                      </tr>
                    ) : (
                      fangates.slice(0, 30).map((f) => (
                        <tr
                          key={f.id}
                          className="border-t border-[#18181B]"
                          data-testid={`fangate-row-${f.id}`}
                        >
                          <td className="px-4 py-3 text-xs text-[#D4D4D8]">
                            {f.album_title}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-[#A1A1AA]">
                            {f.session_masked}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              {['spotify', 'soundcloud', 'instagram', 'tiktok'].map((p) => (
                                <span
                                  key={p}
                                  className={`w-2 h-2 rounded-full ${
                                    f[`${p}_ok`] ? 'bg-[#00F0FF]' : 'bg-[#27272A]'
                                  }`}
                                  title={p}
                                />
                              ))}
                              <span className="text-[10px] text-[#71717A] ml-1">
                                {f.socials_completed}/4
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {f.is_unlocked ? (
                              <span className="text-[10px] uppercase tracking-wider text-[#00F0FF]">
                                Unlocked
                              </span>
                            ) : (
                              <span className="text-[10px] uppercase tracking-wider text-[#52525B]">
                                In progress
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-[#71717A]">
                            {formatDate(f.created_at)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Order Detail Drawer */}
        {selectedOrder && (
          <div
            className="fixed inset-0 z-[70] flex justify-end bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedOrder(null)}
            data-testid="order-drawer"
          >
            <div
              className="w-full max-w-md h-full bg-[#0B0B0D] border-l border-[#18181B] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-[#18181B]">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Order</p>
                <p className="font-mono text-sm text-[#D4D4D8]" data-testid="drawer-order-id">
                  {selectedOrder.id}
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${statusChip(selectedOrder.business_status || selectedOrder.status)}`}
                  >
                    {selectedOrder.business_status || 'Pending'}
                  </span>
                  <span className="text-xs text-[#71717A]">
                    {formatDate(selectedOrder.created_at)}
                  </span>
                </div>
                <p className="text-2xl font-heading font-medium mt-3">
                  {formatPhp(selectedOrder.total_amount)}
                </p>
              </div>

              <div className="p-6 space-y-4 text-sm">
                {(selectedOrder.email || selectedOrder.full_name) && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Buyer</p>
                    <p className="text-white" data-testid="drawer-buyer-name">{selectedOrder.full_name || '—'}</p>
                    <p className="text-xs text-[#A1A1AA]" data-testid="drawer-buyer-email">{selectedOrder.email || '—'}</p>
                  </div>
                )}
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">
                    Session ID (browser)
                  </p>
                  <p className="font-mono text-xs text-[#A1A1AA] break-all">
                    {selectedOrder.session_id}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">
                    Maya Payment ID
                  </p>
                  <p className="font-mono text-xs text-[#A1A1AA] break-all">
                    {selectedOrder.maya_payment_id || '—'}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">
                    Items ({selectedOrder.items?.length || 0})
                  </p>
                  <ul className="space-y-2">
                    {(selectedOrder.items || []).map((it, i) => (
                      <li
                        key={i}
                        className="flex items-center justify-between bg-[#0F0F11] border border-[#18181B] rounded-md px-3 py-2"
                      >
                        <span className="truncate text-sm">{it.album_title}</span>
                        <span className="text-xs text-[#A1A1AA] shrink-0">
                          {formatPhp(it.price)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {selectedOrderDetail?.download_links && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">
                      Download links ({selectedOrderDetail.download_links.length})
                    </p>
                    <ul className="space-y-2">
                      {selectedOrderDetail.download_links.map((dl, i) => {
                        const expired =
                          new Date(dl.expires_at).getTime() < Date.now();
                        return (
                          <li
                            key={i}
                            className="bg-[#0F0F11] border border-[#18181B] rounded-md px-3 py-2 text-xs"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span
                                className={`uppercase tracking-widest text-[10px] font-medium ${
                                  dl.accessed
                                    ? 'text-[#00F0FF]'
                                    : expired
                                    ? 'text-red-400'
                                    : 'text-[#A1A1AA]'
                                }`}
                              >
                                {dl.accessed ? 'accessed' : expired ? 'expired' : 'unused'}
                              </span>
                              <span className="text-[#71717A]">
                                {formatDate(dl.expires_at)}
                              </span>
                            </div>
                            <p className="font-mono text-[10px] text-[#71717A] truncate">
                              {dl.token}
                            </p>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-[#18181B] flex justify-between">
                {selectedOrder.maya_redirect_url && (
                  <a
                    href={selectedOrder.maya_redirect_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-[#A1A1AA] hover:text-white"
                  >
                    Maya checkout <ExternalLink size={12} />
                  </a>
                )}
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="text-xs text-[#A1A1AA] hover:text-white ml-auto"
                  data-testid="close-drawer-btn"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardPage;
